import {
  MutationCache,
  QueryCache,
  QueryClient,
  queryOptions,
  type DefaultOptions,
  type QueryKey,
} from "@tanstack/react-query";
import { api, ApiError, requestApi } from "./api";
import {
  CallInviteResponseSchema,
  GroupInviteResponseSchema,
  DeviceSignInReviewResponseSchema,
  MessagesResponseSchema,
  PeopleResponseSchema,
  PersonResponseSchema,
  PushResponseSchema,
  SessionsResponseSchema,
  type ApiRequest,
} from "../../shared/api";
import { isLiveCall } from "../../shared/calls";
import {
  RegistrationProtectionSchema,
  SnapshotSchema,
} from "../../shared/schemas";
import type {
  AuthenticatedSnapshot,
  CallView,
  ChatMessage,
  Person,
  RealtimeEvent,
  Snapshot,
} from "../../shared/types";

// A message this browser is still sending, or could not send, has the id the
// server will store it under. It lives only in this cache, so a reload never
// replays it.
export type CachedMessage = ChatMessage & { outgoing?: "sending" | "failed" };
// The newest messages and any older pages read since, without a gap.
export type Conversation = { messages: CachedMessage[]; hasOlder: boolean };
export type OutgoingMessage = Omit<
  Extract<ApiRequest, { action: "message" }>,
  "action"
>;

export const keys = {
  snapshot: ["snapshot"] as const,
  people: (userId: string | null) => ["people", userId] as const,
  search: (userId: string | null, term: string) =>
    [...keys.people(userId), term] as const,
  // Whoever opens it sees their own friendship or request with its owner.
  profileLink: (userId: string | null, code: string) =>
    ["profile-link", userId, code] as const,
  deviceSignIn: (userId: string, code: string) =>
    ["device-sign-in", userId, code] as const,
  sessions: (userId: string) => ["sessions", userId] as const,
  conversations: (userId: string) => ["messages", userId] as const,
  messages: (userId: string, peerId: string) =>
    [...keys.conversations(userId), peerId] as const,
  protection: ["registration"] as const,
  invite: (linkId: string) => ["invite", linkId] as const,
  groupInvite: (code: string) => ["group-invite", code] as const,
  push: (userId: string | null) => ["push", userId] as const,
  actions: ["account-action"] as const,
  send: ["send-message"] as const,
  read: ["read"] as const,
  pushActions: ["push-action"] as const,
};

function retryQuery(failureCount: number, error: Error) {
  if (failureCount >= 2) return false;
  return (
    error instanceof ApiError && (error.status >= 500 || error.status === 408)
  );
}

const defaultOptions = {
  queries: {
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: retryQuery,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  },
  // Never replay messages, account changes or single-use verification tokens.
  mutations: { retry: false, networkMode: "always", gcTime: 0 },
} satisfies DefaultOptions;

export function createQueryClient() {
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError(error) {
        if (error instanceof ApiError && error.status === 401)
          setSession(client, signedOut);
      },
    }),
    // Every account action's onMutate returns the session it started under.
    mutationCache: new MutationCache({
      onError(error, _variables, startedBy) {
        if (!(error instanceof ApiError)) return;
        if (error.status === 403)
          void client.invalidateQueries({ queryKey: keys.snapshot });
        if (
          error.status === 401 &&
          typeof startedBy === "string" &&
          isCurrentUser(client, startedBy)
        )
          setSession(client, signedOut);
      },
    }),
    defaultOptions,
  });
  return client;
}
// The admin panel's cache follows the same rules but is its own, made when
// the panel opens and dropped when it closes, so admin data never mixes with
// an account's cached data.
export const createAdminQueryClient = () => new QueryClient({ defaultOptions });

export const requestSignal = (signal?: AbortSignal) =>
  signal
    ? AbortSignal.any([signal, AbortSignal.timeout(15_000)])
    : AbortSignal.timeout(15_000);

export const snapshotOptions = () =>
  queryOptions({
    queryKey: keys.snapshot,
    queryFn: ({ signal }) =>
      api(SnapshotSchema, undefined, "", requestSignal(signal)),
  });

// The account a whole username finds, if it allows being found.
export const peopleOptions = (userId: string | null, term: string) =>
  queryOptions({
    queryKey: keys.search(userId, term),
    queryFn: async ({ signal }) => {
      const data = await api(
        PeopleResponseSchema,
        undefined,
        `?q=${encodeURIComponent(term)}`,
        requestSignal(signal),
      );
      return data.people;
    },
    enabled: !!term,
    gcTime: 60_000,
  });

export const messagesOptions = (
  client: QueryClient,
  userId: string,
  peerId: string,
) =>
  queryOptions({
    queryKey: keys.messages(userId, peerId),
    queryFn: async ({ signal }) => {
      const key = keys.messages(userId, peerId);
      const known = new Set(
        client.getQueryData<Conversation>(key)?.messages.map((m) => m.id),
      );
      const data = await api(
        MessagesResponseSchema,
        undefined,
        `?messages=${encodeURIComponent(peerId)}`,
        requestSignal(signal),
      );
      const cached = client.getQueryData<Conversation>(key);
      // Older pages stay while this page still reaches them. After a long
      // outage it doesn't, and the conversation can't show a gap.
      const oldest = data.messages[0];
      if (cached && data.hasOlder && oldest && known.has(oldest.id))
        return {
          messages: mergeMessages(cached.messages, data.messages),
          hasOlder: cached.hasOlder,
        };
      // Preserve unsent messages and events/POST confirmations delivered while
      // the history was loading. The server's copy of a message wins.
      const arrived =
        cached?.messages.filter((m) => !known.has(m.id) || m.outgoing) ?? [];
      return { ...data, messages: mergeMessages(arrived, data.messages) };
    },
    enabled: !!userId && !!peerId,
  });

export async function loadOlderMessages(
  client: QueryClient,
  userId: string,
  peerId: string,
) {
  const key = keys.messages(userId, peerId);
  const oldest = () =>
    client.getQueryData<Conversation>(key)?.messages.find((m) => !m.outgoing)
      ?.id;
  const before = oldest();
  if (!before) return;
  const data = await api(
    MessagesResponseSchema,
    undefined,
    `?messages=${encodeURIComponent(peerId)}&before=${encodeURIComponent(before)}`,
    requestSignal(),
  );
  // A newer read may have replaced the conversation, or the session ended.
  if (oldest() !== before) return;
  client.setQueryData<Conversation>(
    key,
    (old) =>
      old && {
        messages: mergeMessages(old.messages, data.messages),
        hasOlder: data.hasOlder,
      },
  );
}

export const profileLinkOptions = (userId: string | null, code: string) =>
  queryOptions({
    queryKey: keys.profileLink(userId, code),
    queryFn: async ({ signal }) => {
      const data = await api(
        PersonResponseSchema,
        undefined,
        `?profile=${encodeURIComponent(code)}`,
        requestSignal(signal),
      );
      return data.person;
    },
    enabled: !!code,
  });

// Where this account is signed in. Other devices coming and going refresh it.
export const sessionsOptions = (userId: string) =>
  queryOptions({
    queryKey: keys.sessions(userId),
    queryFn: async ({ signal }) =>
      (
        await api(
          SessionsResponseSchema,
          { action: "sessions" },
          "",
          requestSignal(signal),
        )
      ).sessions,
  });

// What is asking to sign in, once per visit: each read spends the strict limit
// that also holds back guessing, and the answer goes with the screen.
export const deviceSignInOptions = (userId: string, code: string) =>
  queryOptions({
    queryKey: keys.deviceSignIn(userId, code),
    queryFn: ({ signal }) =>
      api(
        DeviceSignInReviewResponseSchema,
        { action: "device-sign-in-review", code },
        "",
        requestSignal(signal),
      ),
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

// The host's presence decides whether joining can ring, so refresh it on focus.
export const inviteOptions = (linkId: string) =>
  queryOptions({
    queryKey: keys.invite(linkId),
    queryFn: ({ signal }) =>
      api(
        CallInviteResponseSchema,
        undefined,
        `?link=${encodeURIComponent(linkId)}`,
        requestSignal(signal),
      ),
    enabled: !!linkId,
    staleTime: 0,
  });

export const groupInviteOptions = (code: string) =>
  queryOptions({
    queryKey: keys.groupInvite(code),
    queryFn: ({ signal }) =>
      api(
        GroupInviteResponseSchema,
        undefined,
        `?group=${encodeURIComponent(code)}`,
        requestSignal(signal),
      ),
    staleTime: 0,
  });

// What this session saved for its device, which only this device changes.
export const pushOptions = (userId: string | null) =>
  queryOptions({
    queryKey: keys.push(userId),
    queryFn: ({ signal }) =>
      requestApi(
        "/api/push",
        PushResponseSchema,
        undefined,
        requestSignal(signal),
      ),
    enabled: !!userId,
  });

export const protectionOptions = () =>
  queryOptions({
    queryKey: keys.protection,
    queryFn: ({ signal }) =>
      requestApi(
        "/api/registration",
        RegistrationProtectionSchema,
        undefined,
        requestSignal(signal),
      ),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

export function clearAccountQueries(
  client: QueryClient,
  userId?: string | null,
) {
  const prefixes =
    userId === undefined
      ? [
          ["people"],
          ["profile-link"],
          ["device-sign-in"],
          ["sessions"],
          ["messages"],
          ["push"],
        ]
      : [
          keys.people(userId),
          ["profile-link", userId],
          ["device-sign-in", userId],
          ["sessions", userId],
          ["messages", userId],
          keys.push(userId),
        ];
  for (const queryKey of prefixes) {
    void client.cancelQueries({ queryKey });
    client.removeQueries({ queryKey });
  }
}

// Revoked friendships must also evict cached histories of closed chats.
export function evictClosedConversations(
  client: QueryClient,
  snapshot: AuthenticatedSnapshot,
) {
  const friends = new Set(snapshot.friends.map((friend) => friend.id));
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: keys.conversations(snapshot.me.id) })) {
    if (query.queryKey[2] && !friends.has(query.queryKey[2] as string)) {
      void client.cancelQueries({ queryKey: query.queryKey, exact: true });
      client.removeQueries({ queryKey: query.queryKey, exact: true });
    }
  }
}

export function setSession(client: QueryClient, snapshot: Snapshot) {
  void client.cancelQueries({ queryKey: keys.snapshot });
  clearAccountQueries(client);
  client.setQueryData(keys.snapshot, snapshot);
}
export const signedOut: Snapshot = {
  authenticated: false,
  me: null,
  guest: null,
};
// The account, or the call-link guest, that this browser is signed in as.
export const sessionId = (snapshot: Snapshot | undefined) =>
  snapshot?.authenticated ? snapshot.me.id : (snapshot?.guest?.id ?? null);

export function isCurrentUser(client: QueryClient, userId: string) {
  return sessionId(client.getQueryData<Snapshot>(keys.snapshot)) === userId;
}

export function refreshAccount(client: QueryClient, userId: string) {
  if (!isCurrentUser(client, userId)) return;
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.snapshot }),
    client.invalidateQueries({ queryKey: keys.people(userId) }),
    client.invalidateQueries({ queryKey: keys.sessions(userId) }),
  ]);
}

function mergeMessages(existing: CachedMessage[], incoming: CachedMessage[]) {
  const messages = new Map(existing.map((message) => [message.id, message]));
  for (const message of incoming) messages.set(message.id, message);
  // Unsent messages stay last, since this device's clock can differ from the server's.
  return [...messages.values()].sort(
    (a, b) =>
      Number(!!a.outgoing) - Number(!!b.outgoing) ||
      a.created_at - b.created_at,
  );
}

const addMessage = (
  client: QueryClient,
  key: QueryKey,
  message: CachedMessage,
) =>
  client.setQueryData<Conversation>(key, (old) => ({
    messages: mergeMessages(old?.messages ?? [], [message]),
    hasOlder: old?.hasOlder ?? false,
  }));

function updateFriends(
  client: QueryClient,
  update: (friend: Person) => Person,
) {
  // An older snapshot response must not overwrite the newer pushed state.
  const fetching =
    client.getQueryState(keys.snapshot)?.fetchStatus === "fetching";
  if (fetching)
    void client.cancelQueries({ queryKey: keys.snapshot, exact: true });
  client.setQueryData<Snapshot>(keys.snapshot, (old) =>
    old?.authenticated ? { ...old, friends: old.friends.map(update) } : old,
  );
  if (fetching)
    void client.invalidateQueries({ queryKey: keys.snapshot, exact: true });
}

export function clearUnread(client: QueryClient, peerId: string) {
  updateFriends(client, (friend) =>
    friend.id === peerId && friend.unread ? { ...friend, unread: 0 } : friend,
  );
}

export function cacheMessage(
  client: QueryClient,
  userId: string,
  message: ChatMessage,
) {
  if (!isCurrentUser(client, userId)) return;
  const peerId = message.sender === userId ? message.recipient : message.sender;
  if (message.sender !== userId && message.recipient !== userId) return;
  updateFriends(client, (friend) => {
    const last = friend.last_message;
    // POST confirmations and WebSocket echoes deliver the same message twice.
    if (
      friend.id !== peerId ||
      (last && (last.id === message.id || last.created_at > message.created_at))
    )
      return friend;
    return {
      ...friend,
      last_message: message,
      // Replying reads the conversation, as it does on the server.
      unread: message.sender === peerId ? (friend.unread ?? 0) + 1 : 0,
    };
  });
  const key = keys.messages(userId, peerId);
  // An unvisited conversation still needs its full history on first opening.
  if (!client.getQueryCache().find({ queryKey: key, exact: true })) return;
  addMessage(client, key, message);
}

// Only the conversation on screen has an observer.
export const isChatOpen = (
  client: QueryClient,
  userId: string,
  peerId: string,
) =>
  !!client
    .getQueryCache()
    .find({ queryKey: keys.messages(userId, peerId), exact: true })
    ?.getObserversCount();

export function cacheOutgoing(
  client: QueryClient,
  userId: string,
  { id, peer, body }: OutgoingMessage,
) {
  addMessage(client, keys.messages(userId, peer), {
    id,
    sender: userId,
    recipient: peer,
    body,
    created_at: Date.now(),
    outgoing: "sending",
  });
}

export function failOutgoing(
  client: QueryClient,
  userId: string,
  { id, peer }: OutgoingMessage,
) {
  client.setQueryData<Conversation>(
    keys.messages(userId, peer),
    (old) =>
      old && {
        ...old,
        // The socket can confirm a message whose request then fails.
        messages: old.messages.map((m) =>
          m.id === id && m.outgoing ? { ...m, outgoing: "failed" } : m,
        ),
      },
  );
}

export function discardOutgoing(
  client: QueryClient,
  userId: string,
  { id, peer }: OutgoingMessage,
) {
  client.setQueryData<Conversation>(
    keys.messages(userId, peer),
    (old) =>
      old && { ...old, messages: old.messages.filter((m) => m.id !== id) },
  );
}

export function applyRealtimeEvent(
  client: QueryClient,
  userId: string,
  event: RealtimeEvent,
) {
  if (!isCurrentUser(client, userId)) return;
  switch (event.type) {
    case "ready":
      void refreshAccount(client, userId);
      // Reconcile messages missed during the WebSocket outage, including closed chats.
      void client.invalidateQueries({ queryKey: keys.conversations(userId) });
      break;
    case "sync":
      void refreshAccount(client, userId);
      break;
    case "presence": {
      // An older HTTP response must not overwrite the newer pushed presence.
      const pending = [
        ...client.getQueryCache().findAll({ queryKey: keys.snapshot }),
        ...client.getQueryCache().findAll({ queryKey: keys.people(userId) }),
      ].filter((query) => query.state.fetchStatus === "fetching");
      for (const query of pending)
        void client.cancelQueries({ queryKey: query.queryKey, exact: true });
      const update = (people: Person[]) =>
        people.map((p) =>
          p.id === event.personId
            ? {
                ...p,
                status: event.status,
                last_active_at: event.last_active_at,
                call_notifications: event.call_notifications,
              }
            : p,
        );
      client.setQueryData<Snapshot>(keys.snapshot, (old) =>
        old?.authenticated
          ? {
              ...old,
              friends: update(old.friends),
              requests: update(old.requests),
            }
          : old,
      );
      client.setQueriesData<Person[]>(
        { queryKey: keys.people(userId) },
        (old) => old && update(old),
      );
      for (const query of pending)
        void client.invalidateQueries({
          queryKey: query.queryKey,
          exact: true,
        });
      break;
    }
    case "message":
      cacheMessage(client, userId, event.message);
      break;
    case "read":
      clearUnread(client, event.personId);
      break;
    case "read-receipt":
      updateFriends(client, (friend) =>
        friend.id === event.personId
          ? {
              ...friend,
              read_through: Math.max(
                friend.read_through ?? 0,
                event.read_through,
              ),
            }
          : friend,
      );
      break;
    case "call": {
      const active = isLiveCall(event.call.status);
      const next = (current: CallView | null) =>
        active ? event.call : current?.id === event.call.id ? null : current;
      client.setQueryData<Snapshot>(keys.snapshot, (old) =>
        old?.authenticated
          ? { ...old, active: next(old.active) }
          : old?.guest
            ? {
                ...old,
                guest: { ...old.guest, active: next(old.guest.active) },
              }
            : old,
      );
      if (!active) void client.invalidateQueries({ queryKey: keys.snapshot });
      break;
    }
    case "session-ended":
      setSession(client, signedOut);
      break;
    case "new-sign-in":
      void client.invalidateQueries({ queryKey: keys.sessions(userId) });
      break;
    // Signals belong to the call, and the rest to the notice; none touches the cache.
    case "signal":
    case "error":
    case "sign-in-method-changed":
      break;
  }
}
